import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { plainToInstance } from 'class-transformer'
import { Dictionary, isNil } from 'lodash'
import { Department } from '@app/features/scoping/models/department.model'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { Role } from '@app/features/scoping/models/role.model'
import { ChangeItem, ChangeItemType, ChangeType } from '../model'
import { getComponentChanges, getDeliverableChanges, getDepartmentChanges, getRoleChanges } from './entity-changes'

// TODO: Do not map change items when preview works properly
export function mapChangeRequestDeliverables(
  scopeDeliverables: Deliverable[] = [],
  previewDeliverables: Deliverable[] = [],
  previewDeliverablesMap: Dictionary<Deliverable> = {},
  changeItemsMap: Dictionary<ChangeItem> = {},
  isTracked?: boolean
): Deliverable[] {
  const deliverableIds = new Set(scopeDeliverables.map((d) => d.id))
  const allDeliverables = [...scopeDeliverables, ...previewDeliverables.filter((p) => !deliverableIds.has(p.id))]

  const deliverables = allDeliverables
    .filter((deliverable: Deliverable) =>
      isTracked
        ? true
        : changeItemsMap[deliverable.changeAuditData?.identity || deliverable.id]?.changeType !== ChangeType.REMOVED
    )
    .map((deliverable: Deliverable) =>
      mapChangeRequestDeliverable(deliverable, previewDeliverablesMap[deliverable.id], changeItemsMap, isTracked)
    )

  return deliverables
}

function mapChangeRequestDeliverable(
  deliverable: Deliverable,
  previewDeliverable: Deliverable,
  changeItemsMap: Dictionary<ChangeItem>,
  isTracked?: boolean
): Deliverable {
  const componentIds = new Set(deliverable.components.map((c) => c.id))
  const previewComponentsMap = new Map((previewDeliverable?.components || []).map((c) => [c.id, c]))

  const newComponents = (Object.values(changeItemsMap) || [])
    .filter(
      (changeItem) =>
        changeItem.changeType === ChangeType.ADDED &&
        changeItem.type === ChangeItemType.COMPONENT &&
        (changeItem.parentId === deliverable.id || changeItem.parentId === deliverable.changeAuditData?.identity) &&
        !previewComponentsMap.has(changeItem.data?.data?.id)
    )
    .map((component) => component.data?.data)

  const allComponents = [
    ...deliverable.components,
    ...(previewDeliverable?.components || []).filter((p) => !componentIds.has(p.id)),
    ...newComponents,
  ].filter(
    (component) =>
      !isNil(component) &&
      (isTracked
        ? true
        : changeItemsMap[component.changeAuditData?.identity || component.id]?.changeType !== ChangeType.REMOVED)
  )

  const components = allComponents.map((component) =>
    mapChangeRequestComponent(
      component,
      previewDeliverable?.components?.find((c) => c.id === component.id),
      deliverable,
      changeItemsMap
    )
  )

  return plainToInstance(Deliverable, {
    ...deliverable,
    ...(previewDeliverable || {}),
    components,
    changes: getDeliverableChanges(deliverable, previewDeliverable),
  })
}

function mapChangeRequestComponent(
  component: ScopeComponent,
  previewComponent: ScopeComponent,
  deliverable: Deliverable,
  changeItemsMap: Dictionary<ChangeItem>
): ScopeComponent {
  const changeItem = changeItemsMap[component.changeAuditData?.identity || component.id]
  const changeItemComponent = changeItem?.data?.data || changeItem?.data || changeItem

  const newDepartments = changeItemComponent?.departments?.new
  const departmentIds = new Set(component.departments?.map((d) => d.id))
  const allDepartments = plainToInstance(Department, [
    ...(component.departments || []),
    ...(newDepartments || []).filter((p) => !departmentIds.has(p.id)),
  ])
  const departments = allDepartments
    .map((department) => {
      const isDepartmentRemoved =
        component.departments.some((d) => d.id === department.id) &&
        newDepartments &&
        !newDepartments?.some((d) => d.id === department.id)
      const isDepartmentAdded =
        !component.departments.some((d) => d.id === department.id) &&
        newDepartments?.some((d) => d.id === department.id)

      return mapChangeRequestDepartment(
        department,
        plainToInstance(
          Department,
          newDepartments?.find((d) => d.id === department.id) ||
            previewComponent?.departments?.find((d) => d.id === department.id)
        ),
        isDepartmentRemoved,
        isDepartmentAdded,
        component,
        previewComponent
      )
    })
    .filter((d) => d.roles.length)

  return plainToInstance(ScopeComponent, {
    ...component,
    ...(previewComponent || {}),
    departments,
    deliverable: { id: deliverable.id, changeAuditData: deliverable.changeAuditData },
    changes: getComponentChanges(component, previewComponent),
  })
}

function mapChangeRequestDepartment(
  department: Department,
  previewDepartment: Department,
  isDepartmentRemoved: boolean,
  isDepartmentAdded: boolean,
  component: ScopeComponent,
  previewComponent: ScopeComponent
): Department {
  const roleIds = new Set(department.roles.map((d) => d.id))
  const allRoles = plainToInstance(Role, [
    ...(department.roles || []),
    ...(previewDepartment?.roles || []).filter((p) => !roleIds.has(p.id)),
  ])
  const roles = allRoles.map((role) => {
    const isRoleRemoved =
      department.roles.some((r) => r.id === role.id) && !previewDepartment?.roles?.some((r) => r.id === role.id)
    const isRoleAdded =
      !department.roles.some((r) => r.id === role.id) && previewDepartment?.roles?.some((r) => r.id === role.id)

    return mapChangeRequestRole(
      role,
      previewDepartment?.roles?.find((r) => r.id === role.id),
      isRoleRemoved,
      isRoleAdded,
      component,
      previewComponent
    )
  })

  return plainToInstance(Department, {
    ...department,
    ...(previewDepartment || {}),
    roles,
    isRemoved: isDepartmentRemoved || !roles?.length || roles?.every((r) => r.isRemoved),
    isAdded: isDepartmentAdded,
    changes: getDepartmentChanges(department, previewDepartment, component, previewComponent),
  })
}

function mapChangeRequestRole(
  role: Role,
  previewRole: Role,
  isRemoved: boolean,
  isAdded: boolean,
  component: ScopeComponent,
  previewComponent: ScopeComponent
): Role {
  return plainToInstance(Role, {
    ...role,
    ...(previewRole || {}),
    isRemoved,
    isAdded,
    changes: getRoleChanges(role, previewRole, component, previewComponent),
  })
}
